export const DAY_MS = 86400000;
export const MIN_DAY_WIDTH = 4;
export const MAX_DAY_WIDTH = 64;
export const MIN_ANCHOR = Date.UTC(1900, 0, 1);
export const MAX_ANCHOR = Date.UTC(2200, 11, 31);

export function dateStamp(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}(?:$|T)/.test(value)) return null;
  const day = value.slice(0, 10);
  const stamp = Date.parse(day + 'T00:00:00Z');
  return Number.isFinite(stamp) && new Date(stamp).toISOString().slice(0, 10) === day ? stamp : null;
}
export function clampAnchor(value) {
  return Math.max(MIN_ANCHOR, Math.min(MAX_ANCHOR, Number.isFinite(value) ? value : MIN_ANCHOR));
}
export function clampDayWidth(value) {
  return Math.max(MIN_DAY_WIDTH, Math.min(MAX_DAY_WIDTH, Number.isFinite(value) ? value : 24));
}
export function timelineWindow(anchor, year) {
  const target = Number.isInteger(year) ? year : new Date(clampAnchor(anchor)).getUTCFullYear() - 2;
  const firstYear = Math.max(1900, Math.min(2196, target));
  const start = Date.UTC(firstYear, 0, 1);
  const end = Date.UTC(firstYear + 5, 0, 1);
  return { year: firstYear, start, end, days: (end - start) / DAY_MS };
}
export function rowDates(row) {
  const first = dateStamp(row.startDate || row.date);
  if (first === null) return null;
  const last = dateStamp(row.endDate) ?? first;
  return { start: first, end: Math.max(first, last) };
}
export function initialAnchor(campaign, rows = [], fallback = Date.now()) {
  const from = dateStamp(campaign?.startDate);
  const to = dateStamp(campaign?.endDate);
  const dates = rows.map(rowDates).filter(Boolean).map(d => d.start).sort((a, b) => a - b);
  const inside = dates.find(d => (from === null || d >= from) && (to === null || d <= to));
  return clampAnchor(inside ?? from ?? dates[0] ?? fallback);
}
export function zoomCenterAnchor({ anchor, fromWidth, toWidth, cursorOffset = 0 }) {
  const oldWidth = clampDayWidth(fromWidth), nextWidth = clampDayWidth(toWidth);
  return clampAnchor(anchor + cursorOffset / oldWidth * DAY_MS - cursorOffset / nextWidth * DAY_MS);
}
export function stepAnchor(anchor, scale, direction) {
  const source = clampAnchor(anchor);
  if (scale === 'week') return clampAnchor(source + direction * 7 * DAY_MS);
  const date = new Date(source);
  const monthShift = direction * (scale === 'year' ? 12 : 1);
  const target = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + monthShift, 1));
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  const fraction = source - Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  return clampAnchor(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), Math.min(date.getUTCDate(), last)) + fraction);
}
export function barGeometry(row, start, days) {
  const range = rowDates(row);
  if (!range) return null;
  const first = Math.round((range.start - start) / DAY_MS);
  const last = Math.round((range.end - start) / DAY_MS);
  if (first >= days || last < 0) return null;
  const left = Math.max(0, first);
  return { left, days: Math.min(days - 1, last) - left + 1, clippedStart: first < 0, clippedEnd: last >= days };
}
export function validatePreference(raw) {
  if (!raw || raw.version !== 1 || !Number.isFinite(raw.anchor) || raw.anchor < MIN_ANCHOR || raw.anchor > MAX_ANCHOR ||
    !Number.isFinite(raw.dayWidth) || raw.dayWidth < MIN_DAY_WIDTH || raw.dayWidth > MAX_DAY_WIDTH ||
    !['week', 'month', 'year'].includes(raw.scale) || !['stage', 'location', 'none'].includes(raw.group) ||
    !Number.isFinite(raw.top) || raw.top < 0 || raw.top > 10000000) return null;
  return { version: 1, anchor: raw.anchor, dayWidth: raw.dayWidth, scale: raw.scale, group: raw.group, top: raw.top,
    collapsed: Array.isArray(raw.collapsed) ? raw.collapsed.filter(x => typeof x === 'string' && x.length < 300).slice(0, 1000) : [] };
}
export function readTimelinePreference(storage, key) {
  try { return validatePreference(JSON.parse(storage?.getItem(key))); } catch { return null; }
}
export function writeTimelinePreference(storage, key, value) {
  const safe = validatePreference(value);
  if (!safe || !key) return false;
  try { storage?.setItem(key, JSON.stringify(safe)); return true; } catch { return false; }
}
export function calendarBands(window, scale) {
  const months = [], years = [], weeks = [];
  for (let i = 0; i < 60; i++) {
    const year = window.year + Math.floor(i / 12), month = i % 12;
    const from = Date.UTC(year, month, 1), to = Date.UTC(year, month + 1, 1);
    months.push({ key: year + '-' + month, start: (from - window.start) / DAY_MS, days: (to - from) / DAY_MS, from });
  }
  for (let i = 0; i < 5; i++) {
    const year = window.year + i, from = Date.UTC(year, 0, 1), to = Date.UTC(year + 1, 0, 1);
    years.push({ key: String(year), start: (from - window.start) / DAY_MS, days: (to - from) / DAY_MS, from });
  }
  for (let index = 0; index < window.days;) {
    const date = new Date(window.start + index * DAY_MS);
    const size = Math.min((8 - date.getUTCDay()) % 7 || 7, window.days - index);
    weeks.push({ key: String(index), start: index, days: size, from: window.start + index * DAY_MS, to: window.start + (index + size - 1) * DAY_MS });
    index += size;
  }
  return scale === 'year' ? { upper: years, lower: months } : { upper: months, lower: weeks };
}
